import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import type { IDevConfig } from '../config/Config';

const CreateDevHeaders = ({
  userIdClaim,
  userTypeClaim,
  agencyClaim,
  rolesClaim,
}: IDevConfig): Record<string, string> => {
  const devHeaders: Record<string, string> = {};

  if (userIdClaim !== undefined) {
    devHeaders['x-claim-userId'] = userIdClaim;
  }

  if (userTypeClaim !== undefined) {
    devHeaders['x-claim-userType'] = userTypeClaim;
  }

  if (agencyClaim !== undefined) {
    devHeaders['x-claim-agency'] = agencyClaim;
  }

  if (rolesClaim !== undefined) {
    devHeaders['x-claim-roles'] = rolesClaim;
  }

  return devHeaders;
};

class AuthorizedApiBase {
  private getAuthTokenFunction: (() => Promise<string>) | undefined = undefined;
  private devHeaders: Record<string, string> = {};

  constructor() {}

  protected transformOptions = async (options: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
    if (!this.getAuthTokenFunction) {
      throw new Error('No getAuthTokenFunction has been provided');
    }

    const authToken = await this.getAuthTokenFunction();

    options.headers = {
      ...options.headers,
      ...this.devHeaders,
      Authorization: `Bearer ${authToken}`,
    };

    return Promise.resolve(options);
  };

  public setAuthConfig = (getAuthToken: () => Promise<string>, devConfig: IDevConfig) => {
    this.getAuthTokenFunction = getAuthToken;
    this.devHeaders = CreateDevHeaders(devConfig);
  };

  protected async transformResult<T>(
    url: string,
    response: AxiosResponse,
    processor: (response: AxiosResponse) => T | Promise<T>
  ): Promise<T> {
    const ct = String(response.headers?.['content-type'] ?? '').toLowerCase();
    const data = response.data;

    // If Axios already parsed JSON, avoid double-parse and return directly
    if (ct.includes('application/json') && typeof data === 'object' && data !== null) {
      return data as T;
    }

    // Otherwise, defer to the generated processor (which may JSON.parse)
    return await Promise.resolve(processor(response));
  }
}

export default AuthorizedApiBase;
